import React, { useEffect, useState } from 'react';
import {
	AddContactListContainer,
	AddContactListForm,
	ContactListContainer,
	ContactsContainer
} from '~/modules/components/global/preferences/preferences.css';
import Button from '~/modules/components/global/button';
import {
	TableStyles
} from '~/modules/components/share-feedback/share-feedback-report/share-feedback-report.css';
import { Input, Table } from 'antd';
import EmptyState from '~/modules/components/global/empty-state';
import Icon from '~/modules/components/global/icon';
import { Label } from '~/pages/common.css';
import Modal from '~/modules/components/global/modal';
import ContactPicker from '~/modules/components/global/contact-picker';
import _ from 'lodash';
import confirm from '~/modules/components/global/confirm';
import PropTypes from 'prop-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ContactListsService } from '~/modules/services/contact-lists-service';
import Spinner from '~/modules/components/global/spinner';
import { captureError } from '../../../pixwel/exception-handler.factory';
import ToastService from '~/modules/services/toast-service';

const ContactListPreferences = ({ currentPreferences, isSaving, updatePreferences, session }) => {

	const columns = [
		{
			title: 'Name',
			dataIndex: 'name',
			key: 'name',
			ellipsis: true
		},
		{
			title: 'Action',
			dataIndex: 'name',
			key: 'action',
			width: 250,
			render: (name, row) => (
				<>
					<Button variant="secondary" data-testid="contact-list-edit-button" onClick={() => onEditClick(row)}>
						<Icon use="edit" color="white"/> Edit
					</Button>
					&nbsp;&nbsp;
					<Button variant="danger" data-testid="contact-list-delete-button" onClick={() => onDeleteClick(row)}>
						<Icon use="trash" color="white"/> Delete
					</Button>
				</>
			)
		}
	];
	// eslint-disable-next-line max-len, no-useless-escape
	const EMAIL_REGEX = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

	const [contactListFormToggle, setContactListFormToggle] = useState(false);

	const [name, setName] = useState('');
	const [recipients, setRecipients] = useState([]);

	const [edit, setEdit] = useState(false);

	const formStyle = { content: { overflow: 'inherit' } };

	const queryClient = useQueryClient();

	useEffect(() => {
		// cleanup on unmount: remove query cache
		return () => {
			queryClient.removeQueries(['getGroupsAndUsers']);
		};
	}, []);

	const onFetchData = (d, error) => {
		if(error) {
			return;
		}
		let contacts = [];
		let emails = [];
		if(edit?.recipients?.users) {
			emails = edit.recipients.users?.filter((e) => EMAIL_REGEX.test(e.toLowerCase()));
		}
		if(d.groups) {
			contacts = [...contacts, ...d.groups.nodes];
		}
		if(d.users) {
			contacts = [...contacts, ...d.users.nodes];
		}
		contacts = [...contacts, ...emails];
		setRecipients(contacts);
	};

	const { isFetching } = useQuery(
		['getGroupsAndUsers'],
		() => ContactListsService.getGroupsAndUsers(
			edit?.recipients?.groups?.filter(Boolean),
			edit?.recipients?.users?.filter((e) => !EMAIL_REGEX.test(e.toLowerCase()))
		),
		{
			// The query will not execute until the edit is true
			enabled: !!edit,
			onSettled: onFetchData,
			refetchOnWindowFocus: false
		}
	);

	const isSaveDisabled = () => {
		return isSaving || (_.isEqual(name, '') || _.isEqual(recipients, []))
			|| (_.isEqual(edit.name, name)
					&& _.isEqual(edit.recipients, getIdsFromRecipients(recipients)));
	};

	const getIdsFromRecipients = (recipients) => {
		let result = { groups: [], users: [] };
		recipients.forEach((r) => {
			if(typeof r === 'string') {
				result.users.push(r);
			}
			else if(r.emailNormal) {
				result.users.push(r.id + '');
			}
			else {
				result.groups.push(r.id + '');
			}
		});
		return result;
	};
	const clearForm = () => {
		setContactListFormToggle(false);
		setEdit(false);
		setName('');
		setRecipients([]);
	};

	const updateData = async(updatedPreferences, edit) => {
		try {
			await updatePreferences(updatedPreferences);
			ToastService.create('success', `Custom Group ${edit ? 'edited' : 'saved'} successfully`);
			clearForm();
		}
		catch(err) {
			captureError(err, `Error saving contact list: ${err.message}`);
		}
	};

	const handleSave = async() => {
		let updatedPreferences = currentPreferences;
		if(!Object.prototype.hasOwnProperty.call(currentPreferences, 'contactLists')) {
			updatedPreferences.contactLists=[];
		}
		if(edit) {
			updatedPreferences.contactLists[edit.key] = { name, recipients: getIdsFromRecipients(recipients) };
		}
		else {
			updatedPreferences.contactLists = [
				...(currentPreferences?.contactLists ?? []),
				{ name, recipients: getIdsFromRecipients(recipients) }
			];
		}
		await updateData(updatedPreferences, edit);
	};

	const onEditClick = async(row) => {
		setEdit(row);
		setName(row.name);
		setContactListFormToggle(true);
	};

	const onDeleteClick = async(row) => {
		if(await confirm({
			title: 'Delete Contact List',
			content: `Are you sure you want to delete this contact list ${row?.name}?`,
			confirmText: 'Delete',
			confirmVariant: 'danger',
			cancelText: 'Cancel',
			cancelVariant: 'secondary'
		})) {
			currentPreferences?.contactLists.splice(row.key, 1);
			await updateData(currentPreferences);
		}
	};

	const renderContactListForm = () => {
		const groups = recipients.filter((r) => !r?.emailNormal && typeof r !== 'string');
		const users = recipients.filter((r) => r?.emailNormal || typeof r === 'string');
		return (
			<Modal
				isOpen={contactListFormToggle}
				contentLabel="ContactListForm"
				size={500}
				shouldCloseOnOverlayClick={false}
				onRequestClose={() => setContactListFormToggle(false)}
				style={formStyle}
			>
				<Modal.Header>
					<h3>Custom Group</h3>
				</Modal.Header>

				<Modal.Body style={{ paddingTop: '0' }}>
					{isFetching
						? <Spinner />
						:<AddContactListForm data-testid={'contact-list-form'}>
							<Label>Name</Label>
							<Input
								onChange={(e) => setName(e.target.value)} defaultValue={name}
								data-testid={'contact-list-form-name-input'}
							/>
							<Label>Contacts</Label>
							<ContactsContainer data-testid={'contact-list-form-contacts-input'}>
								<ContactPicker
									contacts={users}
									onUpdate={(contacts) => setRecipients([...groups, ...contacts])}
									exclude={[session]}
									allowGroups={false}
									allowEmails
									allowUsers
									placeholder={'Search for users...'}
								/>
							</ContactsContainer>

							<Label>Groups</Label>
							<ContactsContainer data-testid={'contact-list-form-contacts-input-groups'}>
								<ContactPicker
									contacts={groups}
									onUpdate={(contacts) => setRecipients([...users, ...contacts])}
									exclude={[session]}
									allowGroups
									allowEmails={false}
									allowUsers={false}
									placeholder={'Search for groups...'}
								/>
							</ContactsContainer>
						</AddContactListForm>
					}
				</Modal.Body>
				<Modal.Footer>
					<Button
						variant="secondary"
						lighten
						onClick={() => clearForm()}
						disabled={isSaving}
					>
						Cancel
					</Button>

					<Button
						data-testid={'contact-list-form-save-btn'}
						onClick={() => handleSave()}
						loading={isSaving}
						disabled={isSaveDisabled()}
					>
						Save
					</Button>
				</Modal.Footer>

			</Modal>
		);
	};

	return (
		<ContactListContainer data-testid="contact-list-container">
			<fieldset>
				<AddContactListContainer>
					<Button
						className="add-contact-btn"
						data-testid="add-contact-list-btn"
						onClick={() => setContactListFormToggle(true)}
						loading={false}
						disabled={false}
					>
						Create a new Custom Group
					</Button>
					{renderContactListForm()}
				</AddContactListContainer>
				<TableStyles>
					<Table
						columns={columns}
						dataSource={ _.get(currentPreferences, 'contactLists', []).map((d, key) => ({ ...d, key })) }
						pagination={false}
						sticky={true}
						loading={isSaving}
						scroll={{ y: 350 }}
						locale={{ emptyText: <EmptyState message="No Custom Groups"/> }}
					/>
				</TableStyles>
			</fieldset>
		</ContactListContainer>
	);
};

ContactListPreferences.propTypes = {
	currentPreferences: PropTypes.object.isRequired,
	isSaving: PropTypes.bool.isRequired,
	updatePreferences: PropTypes.func.isRequired,
	session: PropTypes.object.isRequired
};

export default ContactListPreferences;
